import { useState, useEffect } from 'react';
import { tasksAPI } from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { toast } from 'sonner';
import { CheckSquare } from 'lucide-react';
import { AssigneeSelect } from './AssigneeSelect';

/**
 * QuickTaskDialog - Reusable dialog for creating tasks from any CRM entity
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Callback when dialog state changes
 * @param {string} relatedToType - Type of entity (contact, email, call, meeting, product, memo, ai_agent, etc.)
 * @param {string} relatedToId - ID of the related entity
 * @param {string} relatedToName - Human-readable name of the entity
 * @param {function} onTaskCreated - Callback when task is successfully created
 * @param {string} defaultTitle - Optional default title for the task
 */
export function QuickTaskDialog({ 
  open, 
  onOpenChange, 
  relatedToType, 
  relatedToId, 
  relatedToName,
  onTaskCreated,
  defaultTitle = ''
}) {
  const [loading, setLoading] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: defaultTitle,
    description: '',
    priority: 'medium',
    due_date: '',
    assigned_to: '',
    task_type: 'crm'
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTaskForm({
        title: defaultTitle,
        description: '',
        priority: 'medium',
        due_date: '',
        assigned_to: '',
        task_type: 'crm'
      });
    }
  }, [open, defaultTitle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        task_type: taskForm.task_type,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null,
        assigned_to: taskForm.assigned_to || null,
        related_to_type: relatedToType,
        related_to_id: relatedToId,
        related_to_name: relatedToName
      };

      await tasksAPI.create(payload);
      toast.success('Task created successfully');
      onOpenChange(false);
      if (onTaskCreated) {
        onTaskCreated();
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const getEntityLabel = () => {
    const labels = {
      contact: 'Contact',
      company: 'Company',
      deal: 'Deal',
      ticket: 'Ticket',
      email: 'Email',
      call: 'Call',
      meeting: 'Meeting',
      product: 'Product',
      memo: 'Memo/Note',
      ai_agent: 'AI Agent',
      cold_call: 'Cold Call',
      campaign: 'Campaign'
    };
    return labels[relatedToType] || 'Entity';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-blue-600" />
            Create Task
          </DialogTitle>
          <DialogDescription>
            Link task to: <span className="font-medium">{getEntityLabel()}</span>
            {relatedToName && <span className="text-foreground"> - {relatedToName}</span>}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="task-title">Task Title *</Label>
            <Input
              id="task-title"
              value={taskForm.title}
              onChange={(e) => setTaskForm(p => ({ ...p, title: e.target.value }))}
              placeholder="What needs to be done?"
              required
              data-testid="quick-task-title"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="task-description">Description</Label>
            <Textarea
              id="task-description"
              value={taskForm.description}
              onChange={(e) => setTaskForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Task details (optional)"
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select 
                value={taskForm.priority} 
                onValueChange={(v) => setTaskForm(p => ({ ...p, priority: v }))}
              >
                <SelectTrigger data-testid="quick-task-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Task Type</Label>
              <Select 
                value={taskForm.task_type} 
                onValueChange={(v) => setTaskForm(p => ({ ...p, task_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crm">CRM</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="general_support">General Support</SelectItem>
                  <SelectItem value="technical_support">Technical Support</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Due Date</Label>
            <Input
              type="datetime-local"
              value={taskForm.due_date}
              onChange={(e) => setTaskForm(p => ({ ...p, due_date: e.target.value }))}
              data-testid="quick-task-due-date"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Assign To</Label>
            <AssigneeSelect
              value={taskForm.assigned_to}
              onValueChange={(v) => setTaskForm(p => ({ ...p, assigned_to: v === 'unassigned' ? '' : v }))}
              placeholder="Select team member or AI agent"
              includeAI={true}
            />
            <p className="text-xs text-muted-foreground">
              Assign to a team member or AI agent
            </p>
          </div>
          
          <DialogFooter className="pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              data-testid="quick-task-submit"
            >
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default QuickTaskDialog;
